import { Pencil, Plus, X } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useNotify, useRefresh } from "ra-core";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import { type PickedRecord, RecordPicker } from "./RecordPicker";
import { type ControlledDialogProps, useDialogState } from "./useDialogState";

const SELECT_CLASS =
  "border-input h-9 w-full rounded-md border bg-transparent px-3 text-sm shadow-xs";

const ERRORS: Record<string, string> = {
  name_required: "Give the company a name",
  invalid_parent:
    "That parent is not allowed: a company cannot sit under itself, and an LO team must sit under a lender branch",
  unknown_parent: "That parent company could not be found",
  already_a_member: "They are already a member",
  contact_merged: "That contact was merged; pick the surviving one",
};

const errorText = (error: unknown, fallback: string) =>
  ERRORS[(error as { body?: { error?: string } }).body?.error ?? ""] ??
  fallback;

/** New company (W10 H4): name, kind and parent. Opens it after saving. */
export function CompanyCreateDialog(props: ControlledDialogProps = {}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const navigate = useNavigate();
  const notify = useNotify();
  const { isOpen, setIsOpen, showTrigger } = useDialogState(props);
  const [name, setName] = useState("");
  const [kindId, setKindId] = useState("");
  const [parent, setParent] = useState<PickedRecord | null>(null);
  const catalogs = useQuery({
    queryKey: ["catalogs"],
    queryFn: () => dataProvider.getCatalogs(),
    enabled: isOpen,
  });

  const close = () => {
    setIsOpen(false);
    setName("");
    setKindId("");
    setParent(null);
  };

  return (
    <>
      {showTrigger ? (
        <Button size="sm" onClick={() => setIsOpen(true)}>
          New company
        </Button>
      ) : null}
      <Dialog open={isOpen} onOpenChange={(open) => (open ? null : close())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New company</DialogTitle>
            <DialogDescription>
              A brokerage, team, builder, employer, branch or LO team.
            </DialogDescription>
          </DialogHeader>
          <form
            aria-label="New company"
            className="space-y-4"
            onSubmit={async (event) => {
              event.preventDefault();
              try {
                const { id } = await dataProvider.createCompany({
                  name,
                  kind_id: kindId || null,
                  parent_company_id: parent?.id ?? null,
                });
                close();
                navigate(`/companies/${id}/show`);
              } catch (error) {
                notify(errorText(error, "The company could not be saved"), {
                  type: "error",
                });
              }
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="company-name">Name</Label>
              <Input
                id="company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="company-kind">Kind</Label>
              <select
                id="company-kind"
                className={SELECT_CLASS}
                value={kindId}
                onChange={(e) => setKindId(e.target.value)}
              >
                <option value="">—</option>
                {(catalogs.data?.company_kinds ?? []).map((kind) => (
                  <option key={kind.id} value={kind.id}>
                    {kind.label}
                  </option>
                ))}
              </select>
            </div>
            <RecordPicker
              id="company-parent"
              label="Parent company (optional)"
              objectType="company"
              picked={parent}
              onPick={setParent}
              pickedText="Under"
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={!name.trim()}>
                Save company
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Edit a company's name and parent (W10 H4). */
export function CompanyEditForm({
  company,
}: {
  company: {
    id: string;
    name: string;
    parent: { id: string; name: string } | null;
  };
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(company.name);
  const [parent, setParent] = useState<PickedRecord | null>(null);
  const [keepParent, setKeepParent] = useState(true);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setName(company.name);
          setParent(null);
          setKeepParent(true);
          setIsOpen(true);
        }}
      >
        <Pencil className="size-4" />
        Edit company
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit company</DialogTitle>
            <DialogDescription>Its name, and where it sits.</DialogDescription>
          </DialogHeader>
          <form
            aria-label="Edit company"
            className="space-y-3"
            onSubmit={async (event) => {
              event.preventDefault();
              try {
                await dataProvider.updateCompany(company.id, {
                  name,
                  ...(keepParent
                    ? {}
                    : { parent_company_id: parent?.id ?? null }),
                });
                setIsOpen(false);
                refresh();
              } catch (error) {
                notify(errorText(error, "The company could not be saved"), {
                  type: "error",
                });
              }
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="edit-company-name">Name</Label>
              <Input
                id="edit-company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            {keepParent ? (
              <p className="text-sm">
                Parent: <strong>{company.parent?.name ?? "none"}</strong>{" "}
                <Button
                  type="button"
                  size="sm"
                  variant="link"
                  className="h-auto p-0"
                  onClick={() => setKeepParent(false)}
                >
                  Change
                </Button>
              </p>
            ) : (
              <RecordPicker
                id="edit-company-parent"
                label="New parent (leave empty for none)"
                objectType="company"
                excludeId={company.id}
                picked={parent}
                onPick={setParent}
                pickedText="Under"
              />
            )}
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={!name.trim()}>
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** End a membership (W10 H4): kept as history, shown as "left". */
export function EndMembershipButton({
  affiliationId,
  label,
}: {
  affiliationId: string;
  label: string;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 text-muted-foreground hover:text-foreground"
      aria-label={`End ${label}`}
      onClick={async () => {
        try {
          await dataProvider.endAffiliation(affiliationId);
          refresh();
        } catch {
          notify("The membership could not be ended", { type: "error" });
        }
      }}
    >
      <X className="size-4" />
    </Button>
  );
}

/**
 * Add a membership (W10 H4) from either side: on a company page pick the
 * person, on a contact page pick the company.
 */
export function AddMembershipForm(
  props: { companyId: string } | { contactId: string },
) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [isOpen, setIsOpen] = useState(false);
  const [other, setOther] = useState<PickedRecord | null>(null);
  const [role, setRole] = useState("");
  const onCompany = "companyId" in props;
  const title = onCompany ? "Add member" : "Add membership";

  const reset = () => {
    setIsOpen(false);
    setOther(null);
    setRole("");
  };

  if (!isOpen) {
    return (
      <Button
        className="mt-3 -ml-2 text-muted-foreground"
        size="sm"
        variant="ghost"
        onClick={() => setIsOpen(true)}
      >
        <Plus className="size-4" />
        {title}
      </Button>
    );
  }
  return (
    <form
      aria-label={title}
      className="mt-3 max-w-md space-y-3"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!other) return;
        try {
          await dataProvider.addAffiliation({
            contact_id: onCompany ? other.id : props.contactId,
            company_id: onCompany ? props.companyId : other.id,
            role: role.trim() || null,
          });
          reset();
          refresh();
        } catch (error) {
          notify(errorText(error, "The membership could not be added"), {
            type: "error",
          });
        }
      }}
    >
      <RecordPicker
        id={onCompany ? "member-contact" : "member-company"}
        label={onCompany ? "Person" : "Company"}
        objectType={onCompany ? "contact" : "company"}
        picked={other}
        onPick={setOther}
        pickedText="Adding"
      />
      <div className="space-y-1">
        <Label htmlFor="member-role">Role (optional)</Label>
        <Input
          id="member-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!other}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={reset}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
